import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { quizProgressActions } from '../actions/quiz-progress.actions';
import {
    selectAnswers,
    selectIsFinished,
    selectIsPassed,
    selectLoaded,
    selectLoading,
    selectProgress,
    selectQuizId,
    selectRetrying,
    selectSaving,
    selectSelectedOption,
} from '../selectors/quiz-progress.selectors';

@Injectable({
    providedIn: 'root',
})
export class QuizProgressFacade {
    private readonly store = inject(Store);

    // Observable selectors
    readonly quizId$ = this.store.select(selectQuizId);
    readonly progress$ = this.store.select(selectProgress);
    readonly progressNonNullable$ = this.progress$.pipe(filter((p) => p !== null));
    readonly loading$ = this.store.select(selectLoading);
    readonly loaded$ = this.store.select(selectLoaded);
    readonly saving$ = this.store.select(selectSaving);
    readonly retrying$ = this.store.select(selectRetrying);
    readonly isFinished$ = this.store.select(selectIsFinished);
    readonly isPassed$ = this.store.select(selectIsPassed);
    readonly answers$ = this.store.select(selectAnswers);
    readonly selectedOption$ = (questionId: string) => this.store.select(selectSelectedOption(questionId));

    // Signal selectors
    readonly quizId = this.store.selectSignal(selectQuizId);
    readonly progress = this.store.selectSignal(selectProgress);
    readonly loading = this.store.selectSignal(selectLoading);
    readonly loaded = this.store.selectSignal(selectLoaded);
    readonly saving = this.store.selectSignal(selectSaving);
    readonly retrying = this.store.selectSignal(selectRetrying);
    readonly isFinished = this.store.selectSignal(selectIsFinished);
    readonly isPassed = this.store.selectSignal(selectIsPassed);
    readonly answers = this.store.selectSignal(selectAnswers);
    readonly selectedOption = (questionId: string) => this.store.selectSignal(selectSelectedOption(questionId));

    // Actions
    getProgress(quizId: string): void {
        this.store.dispatch(quizProgressActions.getProgress({ quizId }));
    }

    saveAnswer(quizId: string, questionId: string, optionId: number): void {
        this.store.dispatch(quizProgressActions.saveAnswer({ quizId, questionId, optionId }));
    }

    retry(quizId: string): void {
        this.store.dispatch(quizProgressActions.retry({ quizId }));
    }

    clear(): void {
        this.store.dispatch(quizProgressActions.clear());
    }
}
